package com.rylcart.cp.ui.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.rylcart.cp.ui.api.ApiException
import com.rylcart.cp.ui.api.ProvisioningRun
import com.rylcart.cp.ui.api.Tenant
import com.rylcart.cp.ui.components.DangerButton
import com.rylcart.cp.ui.components.SecondaryButton
import com.rylcart.cp.ui.components.StatusBadge
import com.rylcart.cp.ui.state.LocalApp
import com.rylcart.cp.ui.state.Route
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text

@Composable
fun TenantDetailScreen(tenantId: String) {
    val app = LocalApp.current
    val scope = rememberCoroutineScope()
    var tenant by remember { mutableStateOf<Tenant?>(null) }
    var runs by remember { mutableStateOf<List<ProvisioningRun>>(emptyList()) }
    var expandedRunId by remember { mutableStateOf<Long?>(null) }

    suspend fun reload() {
        runCatching { app.api.tenant(tenantId) }.onSuccess { tenant = it }
        runCatching { app.api.runs(tenantId) }.onSuccess { runs = it }
    }

    LaunchedEffect(tenantId) { reload() }

    // Poll every 4s if anything is mid-flight.
    LaunchedEffect(tenant?.status, runs.firstOrNull()?.status) {
        val activelyChanging = tenant?.status in setOf("provisioning", "destroying") ||
                runs.firstOrNull()?.status == "running"
        while (activelyChanging) {
            kotlinx.coroutines.delay(4000)
            reload()
        }
    }

    val t = tenant ?: run {
        Div(attrs = { classes("empty") }) { Text("Loading…") }
        return
    }

    Div(attrs = { classes("row", "between"); style { property("margin-bottom", "20px") } }) {
        Div {
            H2(attrs = { style { property("margin-bottom", "4px") } }) { Text(t.name) }
            Div(attrs = { classes("muted") }) { Code { Text(t.id) }; Text(" · "); StatusBadge(t.status) }
        }
        Div(attrs = { classes("row"); style { property("gap", "8px") } }) {
            SecondaryButton("← All tenants") { app.navigate(Route.Tenants) }
            SecondaryButton("Open store ↗") { window.open("https://${t.host}", "_blank") }
            DangerButton("Destroy") {
                if (window.confirm("Destroy ${t.name}? This wipes all data for ${t.host}.")) {
                    scope.launch {
                        runCatching { app.api.destroyTenant(t.id) }
                            .onSuccess { app.showOk("Teardown queued (run #${it.runId})"); app.navigate(Route.Tenants) }
                            .onFailure { app.showError((it as? ApiException)?.message ?: "destroy failed") }
                    }
                }
            }
        }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Details") }
        DetailRow("Host", t.host) { window.open("https://${t.host}", "_blank") }
        DetailRow("Plan", t.plan)
        DetailRow("Deploy target", deployTargetLabel(t.deployTarget))
        DetailRow("Resources", resourceSummary(t))
        DetailRow("Admin email", t.adminEmail)
        DetailRow("Created", t.createdAt)
        DetailRow("Updated", t.updatedAt)
        t.lastError?.let { DetailRow("Last error", it, isError = true) }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Provisioning runs") }
        if (runs.isEmpty()) {
            Div(attrs = { classes("muted") }) { Text("No runs yet.") }
        } else {
            runs.forEach { run ->
                Div(attrs = { classes("run-row") }) {
                    StatusBadge(run.status)
                    Div { Text(run.action); Div(attrs = { classes("muted"); style { property("font-size", "12px") } }) { Text("#${run.id} · started ${run.startedAt}") } }
                    Div(attrs = { classes("spacer") }) {}
                    run.exitCode?.let { Code(attrs = { classes("muted") }) { Text("exit=$it") } }
                    SecondaryButton(if (expandedRunId == run.id) "Hide log" else "Show log") {
                        expandedRunId = if (expandedRunId == run.id) null else run.id
                    }
                }
                if (expandedRunId == run.id) {
                    Pre(attrs = { classes("run-log") }) { Text(run.logTail ?: "(no log captured)") }
                }
            }
        }
    }
}

private fun deployTargetLabel(target: String): String = when (target) {
    "docker"      -> "This server (Docker)"
    "ssh"         -> "Remote VPS via SSH"
    "directadmin" -> "DirectAdmin"
    else          -> target
}

private fun resourceSummary(t: Tenant): String {
    val cpu = t.cpuLimit ?: "—"
    val mem = t.memoryLimitMb?.let { "$it MB" } ?: "—"
    val disk = t.storageLimitGb?.let { "$it GB" } ?: "—"
    return "$cpu CPU · $mem RAM · $disk disk"
}

@Composable
private fun DetailRow(label: String, value: String, isError: Boolean = false, onClick: (() -> Unit)? = null) {
    Div(attrs = {
        classes("row")
        style { property("padding", "8px 0"); property("border-bottom", "1px solid var(--gray-100)") }
    }) {
        Div(attrs = { style { property("width", "140px"); property("color", "var(--gray-500)"); property("font-size", "13px") } }) { Text(label) }
        if (onClick != null) {
            A(href = "#", attrs = {
                onClick { e -> e.preventDefault(); onClick() }
            }) { Text(value) }
        } else {
            Div(attrs = { style { if (isError) property("color", "var(--error)") } }) { Text(value) }
        }
    }
}
